import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useSuperAdminRentChargesFilter } from '@/features/superAdmin/features/data/rentCharges/hooks/useSuperAdminRentChargesFilter';
import { SuperAdminRentChargeListResponseDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

type Props = {
    suspendFetching?: boolean;
};

export const useSuperAdminRentChargesListApi = ({ suspendFetching }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useSuperAdminRentChargesFilter();
    const {
        take,
        skip,
        startDate,
        endDate,
        tenantId,
        documentNumber,
        hotelId,
        apartmentId,
        orderBy,
        orderDirection,
    } = filter;

    const { data, isLoading, isFetching, error } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.data.rentCharges.queryKeys.list,
            take,
            skip,
            startDate,
            endDate,
            tenantId,
            documentNumber,
            hotelId,
            apartmentId,
            orderBy,
            orderDirection,
        ],
        queryFn: async (): Promise<SuperAdminRentChargeListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.data.rentCharges.endpoints.list,
                {
                    skip,
                    take,
                    startDate: DateTimeHelper.getDateOnly(startDate),
                    endDate: DateTimeHelper.getDateOnly(endDate),
                    tenantId: tenantId ?? undefined,
                    documentNumber: documentNumber ?? undefined,
                    hotelId: hotelId ?? undefined,
                    apartmentId: apartmentId ?? undefined,
                    orderBy: orderBy ?? undefined,
                    orderDirection,
                },
            );
            const response = await axiosInstance.get<SuperAdminRentChargeListResponseDto>(url);
            return response.data;
        },
        enabled: !suspendFetching,
        placeholderData: keepPreviousData,
    });

    return {
        list: data,
        isLoading,
        isFetching,
        error,
    };
};
